import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { BRANDS } from '../content/siteContent';
import { useReducedMotion } from '../hooks/useMotionPrefs';

// Node layout is fully data-driven: positions are computed from BRANDS.length,
// so the system supports 2 → 10+ brands without new animation code.
function nodePositions(count) {
  const cx = 500;
  const cy = 500;
  const radius = count <= 3 ? 300 : 330;
  const step = 360 / Math.max(count, 3);
  return Array.from({ length: count }, (_, i) => {
    const angle = (-90 + i * step) * (Math.PI / 180);
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });
}

export default function Ecosystem() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const positions = nodePositions(BRANDS.length);

  useEffect(() => {
    if (reduced) {
      ref.current.querySelectorAll('.eco-line').forEach((p) => (p.style.strokeDashoffset = 0));
      ref.current.querySelectorAll('.eco-node, .eco-info').forEach((n) => {
        n.style.opacity = 1;
        n.style.transform = 'translate(-50%,-50%) scale(1)';
      });
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 65%',
          toggleActions: 'play none none none',
        },
      });
      tl.timeScale(0.45);
      tl.fromTo('.eco-core', { scale: 0.6, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.12, ease: 'power2.out' }, 0);
      BRANDS.forEach((_, i) => {
        const t = 0.14 + i * 0.26;
        // connection line draws itself
        tl.to(`.eco-line-${i}`, { strokeDashoffset: 0, ease: 'none', duration: 0.12 }, t)
          // node scales 0.5 → 1
          .fromTo(
            `.eco-node-${i}`,
            { opacity: 0, scale: 0.5 },
            { opacity: 1, scale: 1, duration: 0.1, ease: 'back.out(1.6)' },
            t + 0.1
          )
          // info slides in
          .fromTo(`.eco-info-${i}`, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.08 }, t + 0.16);
      });
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="ecosystem"
      data-testid="ecosystem-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <div className="absolute left-5 top-24 z-10 md:left-12 md:top-28">
        <SectionTag number="05" label="Ecosystem" />
      </div>

      <div className="absolute inset-x-0 top-1/2 mx-auto aspect-square w-full max-w-[560px] -translate-y-1/2 md:inset-0 md:top-auto md:h-full md:w-auto md:max-w-[1000px] md:translate-y-0">
        <svg viewBox="0 0 1000 1000" className="absolute inset-0 h-full w-full" aria-hidden="true">
          {positions.map((pos, i) => (
            <path
              key={BRANDS[i].id}
              className={`eco-line eco-line-${i} energy-glow`}
              d={`M 500 500 Q ${(500 + pos.x) / 2 + 30} ${(500 + pos.y) / 2 - 30} ${pos.x} ${pos.y}`}
              fill="none"
              stroke={BRANDS[i].status === 'active' ? '#47a0d9' : 'rgba(255,255,255,0.3)'}
              strokeWidth={BRANDS[i].status === 'active' ? 2 : 1.2}
              strokeDasharray={1}
              pathLength={1}
              strokeDashoffset={1}
            />
          ))}
        </svg>

        {/* Central node */}
        <div
          className="eco-core absolute left-1/2 top-1/2 z-10 flex h-32 w-32 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#47a0d9]/50 bg-[#101010] md:h-44 md:w-44"
          data-testid="ecosystem-core"
        >
          <span className="pulse-ring absolute inset-0 rounded-full" />
          <span className="flex flex-col items-center gap-2 px-3 text-center">
            <img src="/logo-icon.png" alt="Amaya Energy" className="h-8 w-8 md:h-11 md:w-11" />
            <span className="font-display text-xs font-bold leading-tight tracking-[0.06em] text-white md:text-sm">
              Amaya Energy
            </span>
          </span>
        </div>

        {/* Brand nodes — EnergyBrandNode, rendered from data */}
        {BRANDS.map((brand, i) => (
          <div
            key={brand.id}
            className={`eco-node eco-node-${i} absolute z-10 -translate-x-1/2 -translate-y-1/2`}
            style={{ left: `${positions[i].x / 10}%`, top: `${positions[i].y / 10}%` }}
            data-testid={`brand-node-${brand.id}`}
            data-cursor={brand.status === 'active' ? 'EXPLORE' : undefined}
          >
            <div
              className={`flex h-20 w-20 items-center justify-center rounded-full border md:h-28 md:w-28 ${
                brand.status === 'active'
                  ? 'border-[#47a0d9] bg-[#47a0d9]/10'
                  : 'border-dashed border-white/30 bg-white/[0.03]'
              }`}
            >
              <span
                className={`font-display text-2xl font-semibold uppercase md:text-4xl ${
                  brand.status === 'active' ? 'text-[#47a0d9]' : 'text-white/30'
                }`}
              >
                {brand.name.charAt(0)}
              </span>
            </div>
            <div className={`eco-info eco-info-${i} mt-3 w-24 -translate-x-[8%] text-center md:w-48 md:-translate-x-[25%]`}>
              <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-white md:text-sm">
                {brand.name}
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/45">{brand.category}</p>
              {brand.status === 'coming-soon' && (
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#47a0d9]/70">Coming soon</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="absolute bottom-10 left-5 max-w-xs text-[11px] uppercase tracking-[0.22em] text-white/40 md:left-12">
        A living system — every node is a brand, every line a connection
      </p>
    </section>
  );
}
